/**
 * MemberCaseHistoryTab - Chronological timeline of all case episodes across all programs.
 * Shows on the member profile "Case History" tab.
 */

import React, { useState, useEffect } from 'react';
import {
  FileText, Clock, CheckCircle2, Archive, XCircle, AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  caseEpisodesAPI,
  CaseHistoryEntry,
  EpisodeStatus,
} from '@/services/api/caseEpisodes';

interface MemberCaseHistoryTabProps {
  memberId: number;
}

const STATUS_STYLE: Record<EpisodeStatus, { color: string; dotColor: string }> = {
  NEW: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', dotColor: 'bg-blue-500' },
  ACTIVE: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', dotColor: 'bg-green-500' },
  SUSPENDED: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', dotColor: 'bg-yellow-500' },
  COMPLETED: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', dotColor: 'bg-purple-500' },
  CLOSED: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', dotColor: 'bg-gray-400' },
  ARCHIVED: { color: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400', dotColor: 'bg-gray-300' },
};

export default function MemberCaseHistoryTab({ memberId }: MemberCaseHistoryTabProps) {
  const [episodes, setEpisodes] = useState<CaseHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await caseEpisodesAPI.getMemberHistory(memberId);
        setEpisodes(data.episodes || []);
      } catch (err) {
        setError('Failed to load case history');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [memberId]);

  if (loading) {
    return <div className="py-8 text-center text-sm text-gray-500">Loading case history...</div>;
  }

  if (error) {
    return <div className="py-8 text-center text-sm text-red-500">{error}</div>;
  }

  if (episodes.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">No case episodes found for this member</p>
          <p className="text-xs text-gray-400 mt-1">
            Episodes are created when a member is enrolled in an episode-model program (ECM, Doula, CHW)
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group episodes by program
  const grouped = episodes.reduce<Record<string, CaseHistoryEntry[]>>((acc, ep) => {
    const key = `${ep.program_id}:${ep.program_name}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(ep);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Case History ({episodes.length} episode{episodes.length !== 1 ? 's' : ''})
        </h3>
      </div>

      {Object.entries(grouped).map(([key, programEpisodes]) => {
        const programName = key.split(':')[1] || 'Unknown Program';
        const programType = programEpisodes[0]?.program_type;
        const currentEp = programEpisodes.find(e => e.is_current);

        return (
          <Card key={key}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">
                  {programName}
                  {programType && (
                    <span className="ml-2 text-xs font-normal text-gray-500">{programType}</span>
                  )}
                </CardTitle>
                {currentEp && (
                  <Badge variant="outline" className="text-xs">
                    Current: #{currentEp.case_number}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Timeline rail */}
              <div className="relative">
                {programEpisodes.map((ep, idx) => {
                  const isLast = idx === programEpisodes.length - 1;
                  const style = STATUS_STYLE[ep.status];

                  return (
                    <div key={ep.id} className="relative flex items-start pb-4 last:pb-0">
                      {/* Connector line */}
                      {!isLast && (
                        <div className="absolute left-[7px] top-4 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />
                      )}

                      {/* Dot indicator */}
                      <div className={`relative z-10 w-3.5 h-3.5 rounded-full mt-0.5 ring-2 ring-white dark:ring-gray-900 ${style.dotColor} ${ep.is_current ? 'ring-4 ring-blue-100 dark:ring-blue-900' : ''}`} />

                      {/* Episode info */}
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">
                            Case #{ep.case_number}
                          </span>
                          <span className="text-xs text-gray-500">
                            Ep. {ep.episode_number}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.color}`}>
                            {ep.status}
                          </span>
                          {ep.is_current && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-500 text-white">
                              CURRENT
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Opened {ep.opened_date || 'N/A'}
                          </span>
                          {ep.closed_date && (
                            <span>Closed {ep.closed_date}</span>
                          )}
                          {ep.assigned_to_name && (
                            <span>Assigned: {ep.assigned_to_name}</span>
                          )}
                        </div>
                        {ep.closed_reason && (
                          <p className="text-xs text-gray-400 mt-0.5 italic">
                            Reason: {ep.closed_reason}
                          </p>
                        )}
                        {ep.health_plan_member_id && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            HP Member ID: {ep.health_plan_member_id}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
